import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import FaqSection from '../components/FaqSection';
import CategoryGrid from '../components/CategoryGrid';
import AreasSection from '../components/AreasSection';
import ContactCard from '../components/ContactCard';
import CtaPanel from '../components/CtaPanel';
import { brands, masterBrands } from '../data/brands';
import { company } from '../data/company';
import { quotePath } from '../utils/contactLinks';
import './Home.css';

const HOME_FAQ = [
  {
    q: 'Where can I buy ceiling and drywall supplies in George?',
    a: `${company.name} supplies ceiling boards, plasterboard, suspended ceiling grid, brandering, insulation and acoustic products from our showroom and warehouse at ${company.address.street}, George. Trade customers and the general public are welcome.`,
  },
  {
    q: 'Do you deliver outside George?',
    a: 'Yes. We deliver along the Garden Route from Plettenberg Bay and Knysna to Mossel Bay, west along the N2 to Riversdale, Swellendam and Cape Town, north over the Outeniqua Pass to Oudtshoorn and the Klein Karoo, and east to Port Elizabeth (Gqeberha).',
  },
  {
    q: 'Which ceiling board and plasterboard brands do you stock?',
    a: 'We are master distributor for SuperTec, ETEX, Siniat and OWA, and stock Gyproc plasterboard (including RhinoBoard), Siniat and SuperTec boards, plus RhinoROC and Nutec fibre cement boards.',
  },
  {
    q: 'What are your trading hours?',
    a: `Monday to Thursday ${company.hours.weekdays} and Friday ${company.hours.friday}. Call ${company.phones[0].number} for sales.`,
  },
];

const HIGHLIGHTS = [
  {
    icon: 'award',
    title: '30+ Years in the Industry',
    text: 'Led by Niel Kasselman, well-known in the building industry. Accredited Green Building Council Professional for energy-efficient building advice.',
  },
  {
    icon: 'layers',
    title: 'Master Distributor',
    text: 'Official master distributor for SuperTec, ETEX, Siniat and Speciality Design Concepts (OWA). Also distributing KNAUF, CAPCO, Casofour, Isover, Saint-Gobain Gyproc, RhinoROC, Nutec, WoodUpp, Youngman, MecoLadder, Summit, Swartland and iTe.',
  },
  {
    icon: 'truck',
    title: 'Fast Delivery',
    text: 'Quick turnaround times and reliable delivery across the Western and Eastern Cape — from Cape Town to Port Elizabeth and everywhere in between.',
  },
];

function Home() {
  return (
    <div className="home-page">
      <SEO />

      {/* Hero */}
      <section className="home-hero hero-bg" data-track-location="home_hero" style={{ '--hero-bg': 'url(/images/bg-supertec.jpg)' }}>
        <div className="container home-hero-inner">
          <div className="home-hero-copy animate-fade-in-up">
            <h1>
              <span className="h1-kicker">Ceiling &amp; Drywall Supplies in George</span>{' '}
              The Garden Route&apos;s <span className="accent">ceiling &amp; drywall</span> supplier.
            </h1>
            <p className="home-hero-text">
              Ceiling boards, plasterboard, suspended grid, brandering, insulation and acoustic panels — in stock in
              George and delivered from Plettenberg Bay to Cape Town and into the Klein Karoo.
            </p>
            <div className="btn-row">
              <Link to={quotePath} className="btn btn-sky btn-large">
                Request a quote <Icon name="arrow" />
              </Link>
              <Link to="/products" className="btn btn-ghost btn-large">
                Browse products
              </Link>
            </div>
            <dl className="home-stats">
              <div>
                <dt>30+</dt>
                <dd>Years in the industry</dd>
              </div>
              <div>
                <dt>{masterBrands.length}</dt>
                <dd>Master brands</dd>
              </div>
              <div>
                <dt>{brands.length}</dt>
                <dd>Brands stocked</dd>
              </div>
            </dl>
          </div>
          <div className="home-hero-media">
            <img
              src="/images/warehouse.jpg"
              alt={`${company.name} warehouse in George stocked with ceiling boards, plasterboard and insulation`}
              width="1200"
              height="808"
              fetchpriority="high"
            />
            <div className="home-hero-badge">
              <img src="/sabisa.png" alt="" width="44" height="44" />
              <div>
                <strong>SABISA Accredited</strong>
                <small>Vetted interior systems supplier</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="home-brands" aria-label="Brands we distribute">
        <div className="container">
          <div className="home-brands-marquee">
            <div className="home-brands-track">
              {[0, 1].map((copy) => (
                <div className="home-brands-set" key={copy} aria-hidden={copy === 1 ? 'true' : undefined}>
                  {brands.map((brand) => (
                    <Link
                      to={`/partners#${brand.id}`}
                      key={brand.id}
                      title={brand.name}
                      tabIndex={copy === 1 ? -1 : undefined}
                    >
                      <img src={brand.logo} alt={copy === 1 ? '' : brand.name} loading="lazy" />
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="section section-white">
        <div className="container">
          <div className="section-head">
            <span className="pill">What we stock</span>
            <h2 className="section-title">Everything for ceilings, walls &amp; insulation</h2>
            <p className="section-subtitle">
              Master distributor for SuperTec, ETEX, Siniat and OWA — one supplier for the whole job.
            </p>
          </div>
          <CategoryGrid showAllCard />
        </div>
      </section>

      {/* Why us */}
      <section className="section section-light">
        <div className="container">
          <div className="section-head">
            <span className="pill">Why choose us</span>
            <h2 className="section-title">Here to make a difference</h2>
            <p className="section-subtitle">
              Serving the building industry and general public in George and across the Western &amp; Eastern Cape
            </p>
          </div>
          <div className="grid grid-3">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="card">
                <div className="card-icon">
                  <Icon name={h.icon} />
                </div>
                <h3 className="card-title">{h.title}</h3>
                <p className="card-text">{h.text}</p>
              </div>
            ))}
          </div>
          <div className="home-sabisa">
            <img src="/sabisa.png" alt="SABISA Accredited" width="72" height="72" loading="lazy" />
            <div>
              <h3>SABISA Accredited</h3>
              <p>
                The South African Building Interior Systems Association — an independent body that certifies suppliers
                meeting strict standards for product quality, business integrity and industry compliance. Our
                accreditation gives you peace of mind that you&apos;re buying from a vetted, trustworthy supplier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO copy + contact details */}
      <section className="section section-white">
        <div className="container split split-top">
          <div className="prose">
            <span className="pill">George &amp; the Garden Route</span>
            <h2 className="section-title">The Garden Route&apos;s ceiling &amp; drywall suppliers</h2>
            <p>
              {company.name} is a leading ceiling and drywall supplier in George, Western Cape. From our warehouse in
              Joubert Industria we supply builders, ceiling and drywall contractors, shopfitters, developers and
              homeowners with everything for ceilings, partitions and insulation: ceiling boards and ceiling tiles,
              suspended ceiling exposed grid in white, black and grey, plaster grid, plasterboard (gypsum board) from
              Gyproc, Siniat and SuperTec, steel brandering, studs and tracks, roof and ceiling insulation, acoustic
              cladding, skimming plaster and jointing compound.
            </p>
            <p>
              As master distributor for SuperTec, ETEX, Siniat and OWA — and a SABISA Accredited supplier — we offer
              specialist advice, dependable stock and sharp pricing on project quantities, delivered from Plettenberg Bay
              to Cape Town and into the Klein Karoo.
            </p>
            <p className="home-lang-label">Afrikaans &amp; English service</p>
            <p className="home-af">
              <strong lang="af">Ons praat Afrikaans.</strong>{' '}
              <em>
                We serve clients in both languages — every conversation, every consultation, every step of the way.{' '}
                <span lang="af">In die taal wat vir jou werk.</span>
              </em>{' '}
              <br /><br />
              <span lang="af">
                Plafonne, plafonborde, gipsborde, droëmuur-afskortings, brandering en dak- en plafonisolasie in George —
                met aflewering regoor die Tuinroete, Klein-Karoo en Wes-Kaap.
              </span>
            </p>
          </div>
          <ContactCard title="Contact information" detailed />
        </div>
      </section>

      <AreasSection />

      <FaqSection title="Ceiling & Drywall Supplies in George — FAQ" faq={HOME_FAQ} className="section-white" columns={2} />

      <CtaPanel title="Stock up for your next project" text="Build faster. Finish better. Get paid sooner." />
    </div>
  );
}

export default Home;
